import React from 'react';
import { BoltSlashIcon, ClockIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

import { AmsRefKind } from '~/api/graphql';
import { type BrowseList, useAccountSearch, usePolicySearch } from './ams-browser-lists';
import {
  type Account,
  accountSecondary,
  type Capability,
  type IsSelected,
  isTooShortToSearch,
  joinParts,
  type Policy,
  policyLabel,
  type SearchTab,
  type Toggle
} from './ams-browser-model';
import type { RecentPick } from './ams-browser-recents';
import { amsRefKey, entityTypeOfKind } from './ams-ref';
import { entityTypeSearchRowIcon } from './entity-display';
import {
  ContextCrumb,
  ContextEntityRow,
  ContextTab,
  ContextTabs,
  DocumentSectionHeader,
  EmptyState,
  emptyStateIconClass,
  ListSpinner,
  LoadError,
  LoadMoreButton,
  PickerContextBar,
  PickerViewport,
  PinSearchResultRow,
  policyBadges
} from './picker-ui';

/** How far one integration's search for one tab has got, for the tab's counter. */
type ListReport = { loading: boolean; failed: boolean; count: number; more: boolean };

/** The reports of a tab's searches together: loading until every one is in. */
function tally(reports: Record<string, ListReport>, keys: string[]): ListReport {
  const listed = keys.map((key) => reports[key]);
  return {
    loading: listed.some((r) => !r || r.loading),
    failed: listed.some((r) => r?.failed),
    count: listed.reduce((n, r) => n + (r?.count ?? 0), 0),
    more: listed.some((r) => r?.more)
  };
}

const tallyCount = (t: ListReport) =>
  t.loading || t.failed ? undefined : `${t.count}${t.more ? '+' : ''}`;

/** Whether a tab found nothing anywhere, which one empty state says for all. */
const foundNothing = (t: ListReport) => !t.loading && !t.failed && t.count === 0;

function useReport(
  key: string,
  onReport: (key: string, r: ListReport) => void,
  list: BrowseList<unknown>
) {
  const { initialLoading: loading, failed, more } = list;
  const count = list.items.length;
  React.useEffect(() => {
    onReport(key, { loading, failed, count, more });
  }, [key, onReport, loading, failed, count, more]);
}

/**
 * The search results, as Accounts and Policies tabs over every searched AMS,
 * or the user's recents while the query is empty.
 */
export function SearchView(props: {
  capabilities: { loading: boolean; failed: boolean; integrations: Capability[] };
  searched: Capability[];
  query: string;
  tab: SearchTab | 'auto';
  onTab: (tab: SearchTab) => void;
  recents: { queries: string[]; picks: RecentPick[] };
  isSelected: IsSelected;
  onToggle: Toggle;
  onRecentQuery: (query: string) => void;
  onOpenAccount: (capability: Capability, account: Account) => void;
  onOpenPolicy: (capability: Capability, account: Account, policy: Policy) => void;
  /**
   * Set when the account is the answer: each row links rather than opening,
   * and policies are not searched, since a policy number cannot name one.
   */
  onPickAccount?: (capability: Capability, account: Account) => void;
}) {
  const { capabilities, searched, query, onPickAccount } = props;
  const accountOnly = !!onPickAccount;
  const { integrations } = capabilities;
  const [reports, setReports] = React.useState<Record<string, ListReport>>({});
  const onReport = React.useCallback(
    (key: string, r: ListReport) => setReports((previous) => ({ ...previous, [key]: r })),
    []
  );
  const accounts = tally(
    reports,
    searched.map((c) => `accounts:${c.integrationId}`)
  );
  const policies = tally(
    reports,
    searched.map((c) => `policies:${c.integrationId}`)
  );
  // Accounts win while they load and whenever they match or fail, so the tab
  // does not jump to policies and back as the reads come in.
  const tab: SearchTab =
    props.tab !== 'auto'
      ? props.tab
      : foundNothing(accounts) && !policies.loading && policies.count > 0
        ? 'policies'
        : 'accounts';
  // Policies share the result area with accounts, so in account-only mode
  // there is one list and no tabs to choose between.
  const tooShort = !!query && isTooShortToSearch(query);
  const searching =
    !accountOnly && !capabilities.loading && integrations.length > 0 && !!query && !tooShort;
  const ids = React.useId();
  const tabId = (t: SearchTab) => `${ids}-${t}`;

  return (
    <>
      <PickerContextBar>
        {searching ? (
          <ContextTabs label="Search results">
            <ContextTab
              id={tabId('accounts')}
              controls={`${ids}-panel`}
              label="Accounts"
              count={tallyCount(accounts)}
              active={tab === 'accounts'}
              onClick={() => props.onTab('accounts')}
            />
            <ContextTab
              id={tabId('policies')}
              controls={`${ids}-panel`}
              label="Policies"
              count={tallyCount(policies)}
              active={tab === 'policies'}
              onClick={() => props.onTab('policies')}
            />
          </ContextTabs>
        ) : (
          <ContextCrumb>
            {!query && (props.recents.queries.length > 0 || props.recents.picks.length > 0)
              ? 'Recent'
              : 'Accounts'}
          </ContextCrumb>
        )}
      </PickerContextBar>
      <PickerViewport
        panelId={searching ? `${ids}-panel` : undefined}
        labelledBy={searching ? tabId(tab) : undefined}
      >
        {capabilities.failed ? (
          <div className="p-3">
            <LoadError>
              Couldn't load your AMS connections. Try again or attach files manually.
            </LoadError>
          </div>
        ) : !capabilities.loading && integrations.length === 0 ? (
          // The two modes run out of integrations for different reasons, and
          // attaching a file is no answer to having nothing to link.
          <EmptyState
            icon={<BoltSlashIcon className={emptyStateIconClass} />}
            {...(accountOnly
              ? {
                  title: 'No AMS accounts to search',
                  description:
                    'No connected AMS holds accounts this client can be linked to. Connect one under Integrations.'
                }
              : {
                  title: "AMS browsing isn't available",
                  description:
                    'No connected AMS can be browsed here. Attach files manually to continue.'
                })}
          />
        ) : !query ? (
          // Recents are the document picker's: its picks are files and
          // policies, and restoring one means a selection nothing here acts on.
          accountOnly ? (
            <SearchPrompt
              title="Search for an account"
              description="Type a name, email, phone number or lookup code to find the account in your AMS."
            />
          ) : (
            <RecentsPanel
              recents={props.recents}
              integrations={integrations}
              integrationsLoading={capabilities.loading}
              isSelected={props.isSelected}
              onQuery={props.onRecentQuery}
              onToggle={props.onToggle}
            />
          )
        ) : tooShort ? (
          <SearchPrompt
            title="Keep typing"
            description={`Type at least 3 letters of a name, or a full email, phone number${
              accountOnly ? ' or lookup code.' : ', lookup code or policy number.'
            }`}
          />
        ) : capabilities.loading ? (
          <ListSpinner label="Loading your AMS…" />
        ) : (
          <div className="max-h-[45vh] overflow-y-auto">
            {tab === 'accounts' && foundNothing(accounts) && (
              <EmptyState
                icon={<MagnifyingGlassIcon className={emptyStateIconClass} />}
                title="No matching accounts"
                description={`Try a different name, email, phone number${
                  accountOnly ? ' or lookup code.' : ', lookup code, or policy number.'
                }`}
              />
            )}
            {!accountOnly && tab === 'policies' && foundNothing(policies) && (
              <EmptyState
                icon={<MagnifyingGlassIcon className={emptyStateIconClass} />}
                {...(/\d/.test(query)
                  ? {
                      title: 'No matching policies',
                      description: 'Policy search matches a whole policy number, such as HO3-100.'
                    }
                  : {
                      title: 'Search by policy number',
                      description: 'Type a policy number to find a policy directly.'
                    })}
              />
            )}
            {searched.map((capability) => (
              <React.Fragment key={capability.integrationId}>
                <IntegrationAccounts
                  capability={capability}
                  query={query}
                  sectioned={searched.length > 1}
                  hidden={tab !== 'accounts' || foundNothing(accounts)}
                  onReport={onReport}
                  isSelected={props.isSelected}
                  onToggle={props.onToggle}
                  onOpen={(account) => props.onOpenAccount(capability, account)}
                  onPick={onPickAccount && ((account) => onPickAccount(capability, account))}
                />
                {!accountOnly && (
                  <IntegrationPolicies
                    capability={capability}
                    query={query}
                    sectioned={searched.length > 1}
                    hidden={tab !== 'policies' || foundNothing(policies)}
                    onReport={onReport}
                    isSelected={props.isSelected}
                    onToggle={props.onToggle}
                    onOpen={(account, policy) => props.onOpenPolicy(capability, account, policy)}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        )}
      </PickerViewport>
    </>
  );
}

/** What the result area says before anything has been searched for. */
function SearchPrompt(props: { title: string; description: string }) {
  return (
    <div className="flex min-h-64 items-center justify-center">
      <EmptyState
        icon={<MagnifyingGlassIcon className={emptyStateIconClass} />}
        title={props.title}
        description={props.description}
      />
    </div>
  );
}

type SectionProps = {
  capability: Capability;
  query: string;
  sectioned: boolean;
  /** A hidden section keeps searching and reporting, so its tab's counter fills in. */
  hidden: boolean;
  onReport: (key: string, r: ListReport) => void;
  isSelected: IsSelected;
  onToggle: Toggle;
};

function IntegrationAccounts(
  props: SectionProps & {
    onOpen: (account: Account) => void;
    /** Set in account-only mode, where the row is the answer rather than a way in. */
    onPick?: (account: Account) => void;
  }
) {
  const { capability, onPick } = props;
  const list = useAccountSearch(capability, props.query);
  useReport(`accounts:${capability.integrationId}`, props.onReport, list);
  if (props.hidden) return null;
  const pickable = capability.pickableKinds.includes(AmsRefKind.Account);
  return (
    <SearchSection
      list={list}
      noun="accounts"
      system={capability.displayName}
      sectioned={props.sectioned}
    >
      {list.items.map((account) => (
        <PinSearchResultRow
          key={amsRefKey(account.ref)}
          icon={entityTypeSearchRowIcon(entityTypeOfKind(AmsRefKind.Account))}
          title={account.name}
          secondary={accountSecondary(account)}
          selectDisabled={!pickable}
          selected={onPick ? undefined : props.isSelected(account.ref)}
          onToggle={
            onPick
              ? undefined
              : () =>
                  props.onToggle({
                    ref: account.ref,
                    displayName: account.name,
                    capability,
                    accountName: account.name
                  })
          }
          onNavigate={onPick ? () => onPick(account) : () => props.onOpen(account)}
          selectLabel={onPick ? undefined : `Select account ${account.name}`}
          navigateLabel={onPick ? `Link account ${account.name}` : `Open account ${account.name}`}
        />
      ))}
    </SearchSection>
  );
}

function IntegrationPolicies(
  props: SectionProps & { onOpen: (account: Account, policy: Policy) => void }
) {
  const { capability } = props;
  const list = usePolicySearch(capability, props.query);
  useReport(`policies:${capability.integrationId}`, props.onReport, list);
  if (props.hidden) return null;
  const pickable = capability.pickableKinds.includes(AmsRefKind.Policy);
  return (
    <SearchSection
      list={list}
      noun="policies"
      system={capability.displayName}
      sectioned={props.sectioned}
    >
      {list.items.map(({ policy, account }) => {
        const label = policyLabel(policy);
        return (
          <PinSearchResultRow
            key={amsRefKey(policy.ref)}
            icon={entityTypeSearchRowIcon(entityTypeOfKind(AmsRefKind.Policy))}
            title={label}
            secondary={joinParts(account?.name, policy.carrier, policy.lineOfBusiness)}
            badges={policyBadges({ status: policy.status, expiresAt: policy.expirationDate })}
            selectDisabled={!pickable}
            selected={props.isSelected(policy.ref)}
            onToggle={() =>
              props.onToggle({
                ref: policy.ref,
                displayName: label,
                capability,
                accountName: account?.name
              })
            }
            onNavigate={
              account && capability.filesBrowsable ? () => props.onOpen(account, policy) : undefined
            }
            selectLabel={`Select policy ${label}`}
            navigateLabel={`Open policy ${label}`}
          />
        );
      })}
    </SearchSection>
  );
}

/** One integration's results for one tab: loading, failed, or its rows and Load more. */
function SearchSection(props: {
  list: BrowseList<unknown>;
  noun: string;
  system: string;
  sectioned: boolean;
  children: React.ReactNode;
}) {
  const { list, noun, system } = props;
  return (
    <section aria-label={`${system} ${noun}`}>
      {props.sectioned && <DocumentSectionHeader>{system}</DocumentSectionHeader>}
      {list.initialLoading ? (
        <ListSpinner label={`Searching ${system}…`} />
      ) : list.failed ? (
        <div className="p-3">
          <LoadError>Couldn't search {system}. Try again or attach files manually.</LoadError>
        </div>
      ) : (
        <>
          {list.items.length === 0 && props.sectioned && (
            <p className="px-3 py-2.5 text-sm text-neutral-500 dark:text-neutral-400">
              No matching {noun} in {system}.
            </p>
          )}
          <ul className="divide-y divide-neutral-100 dark:divide-neutral-700">{props.children}</ul>
          {list.more && (
            <LoadMoreButton
              onClick={list.loadMore}
              loading={list.loadingMore}
              failed={list.loadMoreFailed}
            />
          )}
        </>
      )}
    </section>
  );
}

/**
 * The empty query's panel, shown without waiting for the capabilities: a
 * recent pick lists at once and can be picked again once its integration is
 * known, and drops out if the agency no longer has it.
 */
function RecentsPanel(props: {
  recents: { queries: string[]; picks: RecentPick[] };
  integrations: Capability[];
  integrationsLoading: boolean;
  isSelected: IsSelected;
  onQuery: (query: string) => void;
  onToggle: Toggle;
}) {
  const picks = props.recents.picks.flatMap((pick) => {
    const capability = props.integrations.find((c) => c.integrationId === pick.ref.integrationId);
    return capability || props.integrationsLoading ? [{ ...pick, capability }] : [];
  });
  if (props.recents.queries.length === 0 && picks.length === 0) {
    return (
      <SearchPrompt
        title="Search for an account or policy"
        description="Type a name, email, phone number or lookup code to find an account, or a policy number to find a policy."
      />
    );
  }
  return (
    <div className="max-h-[45vh] overflow-y-auto">
      {props.recents.queries.length > 0 && (
        <>
          <DocumentSectionHeader>Recent searches</DocumentSectionHeader>
          <ul className="divide-y divide-neutral-100 dark:divide-neutral-700">
            {props.recents.queries.map((q) => (
              <li key={q}>
                <button
                  type="button"
                  onClick={() => props.onQuery(q)}
                  className="flex w-full items-center gap-3 px-3 py-2.5 text-left"
                >
                  <ClockIcon
                    className="size-5 shrink-0 text-neutral-400 dark:text-neutral-500"
                    aria-hidden="true"
                  />
                  <span className="min-w-0 flex-1 truncate text-sm text-neutral-800 dark:text-neutral-200">
                    {q}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
      {picks.length > 0 && (
        <>
          <DocumentSectionHeader>Recently picked</DocumentSectionHeader>
          <ul>
            {picks.map((pick) => (
              <ContextEntityRow
                key={amsRefKey(pick.ref)}
                entityType={entityTypeOfKind(pick.ref.kind)}
                label={pick.displayName}
                secondary={
                  pick.accountName && pick.accountName !== pick.displayName
                    ? pick.accountName
                    : undefined
                }
                hideTypeBadge
                badges={
                  pick.capability && props.integrations.length > 1
                    ? [pick.capability.displayName]
                    : undefined
                }
                selectDisabled={!pick.capability}
                selected={props.isSelected(pick.ref)}
                onToggle={() => {
                  const { capability } = pick;
                  if (capability) props.onToggle({ ...pick, capability });
                }}
                ariaLabel={`Select ${pick.displayName}`}
              />
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
